import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell as TableCellOrig,
  TableHead as TableHeadOrig,
  TableHeader,
  TableRow,
  TableLoading
} from "@/components/ui/table";
import { Route as PurchaseInvoiceRoute } from "../index";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
} from "@radix-ui/react-icons";
import { useNavigate, useRouter } from "@tanstack/react-router";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { tableHeadClass, tableCellClass, tableCellLastRowClass } from "@/components/ui/table-styles";
import PaginationButton from "@/components/ui/pagination-button";
import { cn } from "@/lib/utils";
import { PaymentMethod, PaymentType } from "@/gen/purchase_invoice/common_pb";
import { convertToCurrencyRounded } from "@/utils/currency_converter";
import { usePurchaseInvoiceData } from "../-hooks/usePurchaseInvoiceData";
import { pharmacyClient } from '@/connect';
import { toast } from "@/components/ui/use-toast";

const TableCell = ({ className = "", ...props }) => (
  <TableCellOrig className={cn(tableCellClass, className)} {...props} />
);
const TableHead = ({ className = "", ...props }) => (
  <TableHeadOrig className={cn(tableHeadClass, className)} {...props} />
);

const StyledTableCell = ({ className = "", ...props }) => (
  <TableCell className={cn(tableCellClass, "font-medium", className)} {...props} />
);
const StyledTableHead = ({ className = "", ...props }) => (
  <TableHead className={cn(tableHeadClass, className)} {...props} />
);

export default function Component() {
  const { data, isLoading, error, isFetching } = usePurchaseInvoiceData();
  const invoices = data?.invoices || [];
  const suppliers = data?.suppliers || [];
  const purchaseInvoiceCount = data?.purchaseInvoiceCount || 0;
  const searchParams = PurchaseInvoiceRoute.useSearch();

  const router = useRouter();
  const navigate = useNavigate();
  const [supplierName, setSupplierName] = useState<string>("");

  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [loadedInvoiceItems, setLoadedInvoiceItems] = useState<Record<string, any[]>>({});
  const [loadingItems, setLoadingItems] = useState<Set<string>>(new Set());

  const toggleRowExpansion = async (rowKey: string, invoiceId: string) => {
    if (expandedRows.has(rowKey)) {
      // Collapse
      const newExpanded = new Set(expandedRows);
      newExpanded.delete(rowKey);
      setExpandedRows(newExpanded);
    } else {
      // Expand
      const newExpanded = new Set(expandedRows);
      newExpanded.add(rowKey);
      setExpandedRows(newExpanded);
      
      // Fetch items if not already loaded
      if (!loadedInvoiceItems[rowKey]) {
        const newLoading = new Set(loadingItems);
        newLoading.add(rowKey);
        setLoadingItems(newLoading);
        
        try {
          const response = await pharmacyClient.getPurchaseInvoice({
            purchaseInvoiceId: invoiceId,
            supplierId: searchParams.supplierID || "",
            stockPointId: "",
          });
          
          setLoadedInvoiceItems(prev => ({
            ...prev,
            [rowKey]: response.inventoryItems || []
          }));
        } catch (error) {
          console.error('Error fetching invoice items:', error);
          toast({
            title: 'Error',
            description: 'Failed to fetch invoice items',
            variant: 'destructive',
            duration: 3000,
          });
          setLoadedInvoiceItems(prev => ({
            ...prev,
            [rowKey]: []
          }));
        } finally {
          const newLoading = new Set(loadingItems);
          newLoading.delete(rowKey);
          setLoadingItems(newLoading);
        }
      }
    }
  };

  useEffect(() => {
    const selectedSupplier = suppliers.find(
      (s) => s.id === searchParams.supplierID
    );
    setSupplierName(selectedSupplier ? selectedSupplier.name : "");
  }, [searchParams]);

  const columns = [
    { key: "grn_number", label: "GRN Number" },
    { key: "invoice_no", label: "Invoice No." },
    { key: "total_products", label: "Products" },
    { key: "invoice_date", label: "Invoice Date" },
    { key: "supplier_name", label: "Supplier Name" },
    { key: "created_date", label: "Created Date" },
    { key: "payment_mode", label: "Payment Mode" },
    { key: "payment_type", label: "Payment Type" },
    { key: "total_amount", label: "Total Amount" },
    { key: "due_date", label: "Due Date" },
  ];
  const visibleColumns = columns.reduce((acc, col) => ({ ...acc, [col.key]: true }), {} as Record<string, boolean>);

  return (
    <div className="">
        <div>
          <div className="flex items-center justify-between">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink
                    onClick={() => {
                      navigate({
                        to: "/invoicing",
                      });
                    }}
                    style={{
                      cursor: "pointer",
                    }}
                  >
                    Reports Overview
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>Purchase Invoice List {supplierName !== "" && <span> - {supplierName}</span>}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            <div className="flex gap-4">
              <Button
                size={'sm'}
                onClick={() => {
                  navigate({
                    search: {
                      ...searchParams,
                      open_supplier_picker: true,
                      supplierID: undefined,
                    },
                  });
                }}
              >
                {
                  supplierName !== "" ? `Change Supplier` : `Filter By Supplier`
                }
              </Button>
              {supplierName !== "" && <Button
                size={'sm'}
                onClick={() => {
                  navigate({
                    search: {
                      ...searchParams,
                      open_supplier_picker: false,
                      supplierID: undefined,
                    },
                  });
                }}
              >
                Show All Suppliers
              </Button>}
            </div>
          </div>
          <hr className="my-4" />
          <div className="border rounded-lg bg-white overflow-hidden">
            {isLoading ? (
              <TableLoading columns={columns} visibleColumns={visibleColumns} />
            ) : !data?.invoices || data.invoices.length === 0 ? (
              <div className="flex items-center justify-center p-8">
                <div className="text-sm text-gray-500">No entries found</div>
              </div>
            ) : (
              <>
                <Table className="text-sm">
                  <TableHeader>
                    <TableRow className="hover:bg-inherit">
                      <StyledTableHead>GRN Number</StyledTableHead>
                      <StyledTableHead>Invoice No.</StyledTableHead>
                      <StyledTableHead>Products</StyledTableHead>
                      <StyledTableHead>Invoice Date</StyledTableHead>
                      <StyledTableHead>Supplier Name</StyledTableHead>
                      <StyledTableHead>Created Date</StyledTableHead>
                      <StyledTableHead>Payment Mode</StyledTableHead>
                      <StyledTableHead>Payment Type</StyledTableHead>
                      <StyledTableHead>Total Amount</StyledTableHead>
                      <StyledTableHead>Due Date</StyledTableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map((item, index) => {
                      const rowKey = `pi-${item.id}`;
                      const isExpanded = expandedRows.has(rowKey);
                      const isLastRow = index === invoices.length - 1;
                      
                      return (
                        <React.Fragment key={item.id}>
                          <TableRow className="font-medium">
                            <StyledTableCell 
                              className=""
                              onClick={() => {
                                router.navigate({
                                  to: '/inventory/purchase-invoice/$id',
                                  params: { id: item.id },
                                })
                              }}
                              style={{ cursor: 'pointer' }}
                            >
                              {item.grnNumber}
                            </StyledTableCell>
                            <StyledTableCell 
                              className=""
                              onClick={() => {
                                router.navigate({
                                  to: '/inventory/purchase-invoice/$id',
                                  params: { id: item.id },
                                })
                              }}
                              style={{ cursor: 'pointer' }}
                            >
                              {item.invoiceNumber}
                            </StyledTableCell>
                            <StyledTableCell 
                              className=""
                              onClick={(e: { stopPropagation: () => void; }) => {
                                e.stopPropagation();
                                toggleRowExpansion(rowKey, item.id);
                              }}
                            >
                              <div className="flex items-center gap-2 cursor-pointer hover:text-blue-600 transition-colors">
                                <svg 
                                  className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                  fill="none" 
                                  stroke="currentColor" 
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                                <span>{item.totalItems}</span>
                              </div>
                            </StyledTableCell>
                            <StyledTableCell 
                              className=""
                              onClick={() => {
                                router.navigate({
                                  to: '/inventory/purchase-invoice/$id',
                                  params: { id: item.id },
                                })
                              }}
                              style={{ cursor: 'pointer' }}
                            >
                              {dayjs(item.invoiceDate?.toDate()).format("DD/MM/YYYY")}
                            </StyledTableCell>
                            <StyledTableCell 
                              className=""
                              onClick={() => {
                                router.navigate({
                                  to: '/inventory/purchase-invoice/$id',
                                  params: { id: item.id },
                                })
                              }}
                              style={{ cursor: 'pointer' }}
                            >
                              {item.supplierName}
                            </StyledTableCell>
                            <StyledTableCell 
                              className=""
                              onClick={() => {
                                router.navigate({
                                  to: '/inventory/purchase-invoice/$id',
                                  params: { id: item.id },
                                })
                              }}
                              style={{ cursor: 'pointer' }}
                            >
                              {dayjs(item.createdAt?.toDate()).format("DD/MM/YYYY")}
                            </StyledTableCell>
                            <StyledTableCell 
                              className=""
                              onClick={() => {
                                router.navigate({
                                  to: '/inventory/purchase-invoice/$id',
                                  params: { id: item.id },
                                })
                              }}
                              style={{ cursor: 'pointer' }}
                            >
                              {Object.keys(PaymentMethod).find(
                                (key) => PaymentMethod[key as keyof typeof PaymentMethod] === item.paymentMethod
                              ) || "Unknown"}
                            </StyledTableCell>
                            <StyledTableCell 
                              className=""
                              onClick={() => {
                                router.navigate({
                                  to: '/inventory/purchase-invoice/$id',
                                  params: { id: item.id },
                                })
                              }}
                              style={{ cursor: 'pointer' }}
                            >
                              {Object.keys(PaymentType).find(
                                (key) => PaymentType[key as keyof typeof PaymentType] === item.paymentType
                              ) || "Unknown"}
                            </StyledTableCell>
                            <StyledTableCell 
                              className=""
                              onClick={() => {
                                router.navigate({
                                  to: '/inventory/purchase-invoice/$id',
                                  params: { id: item.id },
                                })
                              }}
                              style={{ cursor: 'pointer' }}
                            >
                                {convertToCurrencyRounded(
                                item.items?.reduce((total: number, item: { inputQuantity: { original: any; }; costFromSupplier: number; }) => {
                                  const quantity = (item.inputQuantity?.original || 0);
                                  return total + (quantity * item.costFromSupplier);
                                }, 0) || 0
                              )}
                            </StyledTableCell>
                            <StyledTableCell 
                              className=""
                              onClick={() => {
                                router.navigate({
                                  to: '/inventory/purchase-invoice/$id',
                                  params: { id: item.id },
                                })
                              }}
                              style={{ cursor: 'pointer' }}
                            >
                              {item.dueDate ? dayjs(item.dueDate.toDate()).format("DD/MM/YYYY") : "N/A"}
                            </StyledTableCell>
                          </TableRow>
                          
                          {isExpanded && (
                            <TableRow className="bg-gray-50">
                              <StyledTableCell 
                                colSpan={10}
                                className={isLastRow ? tableCellLastRowClass : ""}
                              >
                                <div className="py-2 px-4">
                                  <div className="text-sm font-semibold text-gray-700 mb-3">Items in Invoice</div>
                                  <div className="grid gap-2">
                                    {loadingItems.has(rowKey) ? (
                                      <div className="flex items-center justify-center py-4">
                                        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
                                        <span className="ml-2 text-sm text-gray-500">Loading items...</span>
                                      </div>
                                    ) : loadedInvoiceItems[rowKey] && loadedInvoiceItems[rowKey].length > 0 ? (
                                      loadedInvoiceItems[rowKey].map((lineItem, itemIdx: number) => (
                                        <div 
                                          key={itemIdx}
                                          className="flex items-center justify-between py-2 px-3 bg-white rounded-md border"
                                        >
                                          <div className="flex items-center gap-3">
                                            <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 font-semibold text-xs">
                                              {itemIdx + 1}
                                            </div>
                                            <div>
                                              <span className="text-sm font-medium">{lineItem.tradeName}</span>
                                              <span className="text-xs text-gray-500 ml-2">
                                                ({lineItem.unit} {lineItem.unitType})
                                              </span>
                                            </div>
                                          </div>
                                          <div className="flex items-center gap-4">
                                            <span className="text-sm text-gray-600">
                                              Qty: <span className="font-semibold">
                                                {Math.floor(lineItem.inputQuantity?.original || 0)}
                                              </span>
                                            </span>
                                            {lineItem.inputQuantity?.bonus && lineItem.inputQuantity.bonus > 0 && (
                                              <span className="text-sm text-green-600">
                                                Bonus: <span className="font-semibold">{Math.floor(lineItem.inputQuantity.bonus)}</span>
                                              </span>
                                            )}
                                            <span className="text-sm text-gray-600">
                                              Cost: <span className="font-semibold">{convertToCurrencyRounded(lineItem.costFromSupplier)}</span>
                                            </span>
                                          </div>
                                        </div>
                                      ))
                                    ) : (
                                      <div className="text-center py-4 text-sm text-gray-500">
                                        No items found
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </StyledTableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </>
            )}
            <div className="flex items-center justify-between py-4 px-6 text-sm border-t bg-white">
              <p>{purchaseInvoiceCount} <span className="text-gray-500 font-light">count</span></p>
              <div className="flex items-center gap-1">
                <PaginationButton
                  onClick={() => {
                    navigate({
                      search: {
                        ...searchParams,
                        page: 1
                      }
                    });
                  }}
                  disabled={searchParams.page === 1}
                >
                  <DoubleArrowLeftIcon className="h-4 w-4" />
                </PaginationButton>
                <PaginationButton
                  onClick={() => {
                    navigate({
                      search: {
                        ...searchParams,
                        page: searchParams.page - 1
                      }
                    });
                  }}
                  disabled={searchParams.page === 1}
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                </PaginationButton>
                {(() => {
                  const itemsPerPage = searchParams.pageSize;
                  const totalPages = purchaseInvoiceCount > 0 ? Math.ceil(purchaseInvoiceCount / itemsPerPage) : 1;
                  const getPageNumbers = () => {
                    const pages = [];
                    if (totalPages <= 7) {
                      for (let i = 1; i <= totalPages; i++) {
                        pages.push(i);
                      }
                    } else {
                      pages.push(1);
                      if (searchParams.page > 3) pages.push('...');
                      for (let i = Math.max(2, searchParams.page - 1); i <= Math.min(searchParams.page + 1, totalPages - 1); i++) {
                        pages.push(i);
                      }
                      if (searchParams.page < totalPages - 2) pages.push('...');
                      pages.push(totalPages);
                    }
                    return pages;
                  };
                  return getPageNumbers().map((page, idx) => (
                    page === '...'
                      ? <span key={`ellipsis-${idx}`} className="mx-1">...</span>
                      : <PaginationButton
                          key={page}
                          active={searchParams.page === page}
                          onClick={() => {
                            navigate({
                              search: {
                                ...searchParams,
                                page: typeof page === 'number' ? page : searchParams.page
                              }
                            });
                          }}
                        >
                          <span className="text-sm">{page}</span>
                        </PaginationButton>
                  ));
                })()}
                <PaginationButton
                  onClick={() => {
                    navigate({
                      search: {
                        ...searchParams,
                        page: searchParams.page + 1
                      }
                    });
                  }}
                  disabled={purchaseInvoiceCount === 0 || purchaseInvoiceCount <= searchParams.page * searchParams.pageSize}
                >
                  <ChevronRightIcon className="h-4 w-4" />
                </PaginationButton>
                <PaginationButton
                  onClick={() => {
                    navigate({
                      search: {
                        ...searchParams,
                        page: Math.ceil(purchaseInvoiceCount / searchParams.pageSize)
                      }
                    });
                  }}
                  disabled={purchaseInvoiceCount === 0 || purchaseInvoiceCount <= searchParams.page * searchParams.pageSize}
                >
                  <DoubleArrowRightIcon className="h-4 w-4" />
                </PaginationButton>
              </div>
            </div>
          </div>
        </div>
      <Dialog open={searchParams.open_supplier_picker ?? false}>
        <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Select a supplier</DialogTitle>
                <DialogDescription>
                  Select a supplier to filter the purchase invoices
                </DialogDescription>
                <Button
                    variant={'ghost'} 
                    className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
                    onClick={() => {
                      navigate({
                        to: "/reports/purchase-invoice-list/",
                        search: {
                          ...searchParams,
                          open_supplier_picker: false,
                        }
                      })
                    }}
                >
                    <X className="h-4 w-4" />
                    <span className="sr-only">Close</span>
                </Button>
              </DialogHeader>
              <div className="flex flex-col gap-4">
                <Table className="text-sm">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Supplier Name</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {suppliers.map((supplier, index) => {
                      return (
                        <TableRow key={index} className="cursor-pointer" onClick={() => {
                          navigate({
                            to: "/reports/purchase-invoice-list/",
                            search: {
                              ...searchParams,
                              supplierID: supplier.id,
                              open_supplier_picker: false,
                            }
                          })
                        }}>
                          <TableCell>{supplier.name}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}